import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PersonIcon from '@mui/icons-material/Person';
import LanguageSelector from '../languageSelector/LanguageSelector';
import avatar_default from 'assets/img/default-avatar.png';
import userMenuConfig from 'constants/sidebar';
import {
  Avatar,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuList,
  StyledArrowDropDown,
  StyledArrowDropUp,
  StyledMenuItem,
  StyledNavLinkMenu,
  StyledSelector
} from './UserMenuStyle';
import UserMenuItem from './UserMenuItem';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { USER_PROFILE_QUERY } from 'features/userProfile/queries';
import userManager from 'utils/userManager';

function UserMenu({ drawerOpen, language, changeLanguage, withGradient }) {
  const [openAvatar, setOpenAvatar] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const activeRoute = useCallback(routeName => location.pathname.indexOf(routeName) > -1, [location.pathname]);
  const userMenuItems = userMenuConfig;

  const { data } = useQueryWithErrorHandling(USER_PROFILE_QUERY);

  const logout = useCallback(() => {
    userManager.signoutRedirect();
  }, []);

  const openCollapseAvatar = useCallback(
    e => {
      setOpenAvatar(!openAvatar);
      e.preventDefault();
    },
    [openAvatar]
  );

  const userInfo = useMemo(() => data?.userInfo || null, [data]);
  const fullName = `${userInfo?.firstName || ''} ${userInfo?.lastName || ''}`;

  return (
    <List>
      <ListItem>
        <StyledNavLinkMenu to={'/'} withGradient={withGradient} onClick={openCollapseAvatar}>
          <ListItemIcon>
            <Avatar src={userInfo?.userAdditionalData?.avatar || avatar_default} alt='...' />
          </ListItemIcon>
          <Tooltip title={fullName}>
            <ListItemText primary={fullName} disableTypography={true} drawerOpen={drawerOpen} />
          </Tooltip>
          <ListItemIcon>
            {openAvatar ? <StyledArrowDropUp drawerOpen={drawerOpen} /> : <StyledArrowDropDown drawerOpen={drawerOpen} />}
          </ListItemIcon>
        </StyledNavLinkMenu>
        <Collapse in={openAvatar} unmountOnExit>
          <MenuList>
            {userMenuItems.map((userMenu, key) => {
              return (
                <UserMenuItem key={key} userMenu={userMenu} drawerOpen={drawerOpen} activeRoute={activeRoute} withGradient={withGradient} />
              );
            })}
            {userInfo && (
              <>
                <Tooltip disableHoverListener={drawerOpen} title={t('MyProfile')}>
                  <StyledMenuItem>
                    <StyledNavLinkMenu to={'/user'} withGradient={withGradient}>
                      <ListItemIcon>
                        <PersonIcon />
                      </ListItemIcon>
                      <ListItemText primary={t('MyProfile')} disableTypography={true} drawerOpen={drawerOpen} />
                    </StyledNavLinkMenu>
                  </StyledMenuItem>
                </Tooltip>
                <Tooltip disableHoverListener={drawerOpen} title={t('Tooltips.Logout')}>
                  <StyledMenuItem>
                    <StyledNavLinkMenu to={'/'} withGradient={withGradient} onClick={logout}>
                      <ListItemIcon>
                        <PowerSettingsNewIcon />
                      </ListItemIcon>
                      <ListItemText primary={t('Tooltips.Logout')} disableTypography={true} drawerOpen={drawerOpen} />
                    </StyledNavLinkMenu>
                  </StyledMenuItem>
                </Tooltip>
              </>
            )}
            <StyledSelector>
              <LanguageSelector language={language} changeLanguage={changeLanguage} drawerOpen={drawerOpen} />
            </StyledSelector>
          </MenuList>
        </Collapse>
      </ListItem>
    </List>
  );
}

UserMenu.propTypes = {
  drawerOpen: PropTypes.bool.isRequired,
  changeLanguage: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  withGradient: PropTypes.bool.isRequired
};

export default UserMenu;
